import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { api } from "../../utils/api";

export default function ForcePendingXeroDialog({ open, onClose, redemptionIds, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !redemptionIds?.length) {
      setPreflight(null);
      setResult(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api("v1/redemption/force-xero-bills/preflight", "POST", {
          redemptionIds,
          selectedPortal: "Administrator",
        });
        if (!cancelled) {
          setPreflight(response.data?.data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || "Pre-flight check failed.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, redemptionIds]);

  const assessment = preflight?.assessment;
  const allowedNow = assessment?.allowedNow ?? 0;

  const runForce = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api("v1/redemption/force-xero-bills", "POST", {
        redemptionIds,
        selectedPortal: "Administrator",
      });
      setResult(response.data?.data || response.data);
      if (onComplete) onComplete(response.data);
    } catch (e) {
      setError(e?.response?.data?.message || "Force complete failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Force complete Pending Xero (priority queue)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Selected redemptions are pulled out of the cron FIFO queue into a dedicated force lane and
          posted to Xero immediately. Pre-flight uses hard Xero API limits only (not backlog depth).
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Selected: <strong>{redemptionIds?.length || 0}</strong>
        </Typography>
        {loading && !preflight && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <span>Running pre-flight…</span>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {assessment && (
          <Alert severity={allowedNow > 0 ? "info" : "warning"} sx={{ mb: 2 }}>
            {assessment.message}
            {assessment.rateLimitedUntil && (
              <>
                <br />
                Backoff until: {assessment.rateLimitedUntil}
              </>
            )}
            <br />
            Can run now: <strong>{allowedNow}</strong> / {assessment.requestedCount}
            {assessment.dayLimitRemaining != null && (
              <>
                <br />
                Xero day remaining (API header): {assessment.dayLimitRemaining}
              </>
            )}
          </Alert>
        )}
        {result?.processResult && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Posted: {result.processResult.posted ?? 0}, failed: {result.processResult.failed ?? 0}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={loading || !allowedNow}
          onClick={runForce}
        >
          Force complete now ({allowedNow})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
